/**
 * rituals.ts — daily streak + morning/night/mystery claims + offline pet
 * events (PR-15 split of api/client.ts).
 *
 * Owns the "what happened while you weren't here / what should you do
 * today" endpoints. ritualsToday is the source of truth for the streak
 * panel; ritualsClaim awards one of {daily_checkin, morning_bonus,
 * night_bonus, mystery_egg}. petEvents is the offline-event feed
 * (e.g. "your hamster found a sunflower seed at 3am").
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by DailyRitualsPanel, TopBar (streak badge), HomePage side
 *     rail, and the petEventsClient poll loop.
 *   - Backend: backend/src/routes/rituals.js, pet_events.js.
 *
 * 关键约束：
 *   - Every call forwards deviceTz() — morning/night windows reset at
 *     local midnight, not the backend's KL midnight.
 */
import { call, deviceTz } from './_core';

export interface RitualsTodayRes {
  today: string;        // YYYY-MM-DD in Asia/Kuala_Lumpur
  tz: string;
  streak: {
    current: number;
    longest: number;
    total_login_days: number;
    claimed_today_morning: boolean;
    claimed_today_night: boolean;
    claimed_today_mystery: boolean;
    claimed_today_checkin: boolean;
  };
  mystery_egg_available: boolean;
  daily_checkin_available: boolean;
  morning_window: boolean;
  night_window: boolean;
  morning_bonus_available: boolean;
  night_bonus_available: boolean;
}

export interface RitualsClaimRes {
  ok: true;
  claim_id: number | null;
  type: 'daily_checkin' | 'morning_bonus' | 'night_bonus' | 'mystery_egg';
  reward_kind: 'sen' | 'food' | 'decor';
  reward_amount: number;       // sen amount, or item_id when reward_kind != 'sen'
  metadata: {
    message_zh?: string;
    message_en?: string;
    item_code?: string;
    item_id?: number;
    roll_kind?: 'sen' | 'food' | 'decor';
    total_login_days?: number;
  };
}

export interface PetOfflineEvent {
  id: number;
  pet_id: number;
  body_zh: string;
  body_en: string;
  created_at: number;
  read_at: number | null;
  pet_nickname: string;
  breed_id: number;
  species: string;
  breed_name: string;
  sprite_key: string | null;
}

export const ritualsApi = {
  // ─── Daily rituals + offline pet events (PR-3) ─────────────────────────
  // Pass the device's IANA timezone so "today" / "morning window" / etc.
  // line up with the kid's local clock instead of the server's KL default.
  ritualsToday() {
    return call<RitualsTodayRes>(`/api/v1/child/rituals/today?tz=${encodeURIComponent(deviceTz())}`);
  },
  ritualsClaim(type: 'daily_checkin' | 'morning_bonus' | 'night_bonus' | 'mystery_egg') {
    return call<RitualsClaimRes>('/api/v1/child/rituals/claim', {
      method: 'POST', body: JSON.stringify({ type, tz: deviceTz() }),
    });
  },
  petEvents(limit = 5) {
    return call<{ events: PetOfflineEvent[] }>(
      `/api/v1/child/pet-events?limit=${limit}`,
    );
  },
  petEventSeen(id: number) {
    return call<{ ok: true }>(
      `/api/v1/child/pet-events/${id}/seen`,
      { method: 'POST' },
    );
  },
};
